import React, { useEffect, useMemo, useState } from 'react';

import { EmployeeLicenseStatus, States } from '@vestahealthcare/common/enums';
import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  EmployeeLicense,
  EmployeeLicenseItem,
} from '@vestahealthcare/common/models';
import { DATE_FORMAT } from '@vestahealthcare/common/utils/constants';

import { Button, CollapsableSidebar, Select, SwitchGroup } from 'styleguide-v2';
import { SwitchItem } from 'styleguide-v2/src/components/Switch/SwitchGroup';

import { showGlobalError } from 'dash/src/components/GlobalMessage';
import { EmployeeLicenseUpdateParam } from 'dash/src/services/EmployeeServices';

import { COMPACT, EmployeLicensesEdit } from './EmployeLicensesEdit';

type Props = {
  data: {
    compactStates?: States[];
  };
  license?: EmployeeLicenseItem;
  open: boolean;
  onClose: () => void;
  onSubmit: (id: number, params: EmployeeLicenseUpdateParam) => Promise<void>;
};

export const EmployeeLicenseEdit = ({
  data,
  license,
  onClose,
  onSubmit,
  open,
}: Props) => {
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);

  const [psor, setPsor] = useState<States>();
  const [psorOld, setPsorOld] = useState<States>();
  const [compactLicense, setCompactLicense] = useState<boolean>(false);
  const [compactLicenseOld, setCompactLicenseOld] = useState<boolean>(false);
  const [stateLicenses, setStateLicenses] = useState<EmployeeLicense[]>([]);

  const compactLicenseDisabled = useMemo(
    () => !psor || !data.compactStates?.includes(psor),
    [data.compactStates, psor],
  );

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setLoadingButton(false);
    }
  }, [open]);

  useEffect(() => {
    const licenses = [
      license?.compactLicense
        ? { ...license?.compactLicense, state: COMPACT }
        : undefined,
      ...(license?.stateLicenses || []),
    ].filter(Boolean) as EmployeeLicense[];

    const compactLicense =
      !!license?.compactLicense?.status &&
      ![
        EmployeeLicenseStatus.INACTIVE,
        EmployeeLicenseStatus.INACTIVE_DUE_PSOR,
      ].includes(license.compactLicense.status);
    setCompactLicense(compactLicense);
    setCompactLicenseOld(compactLicense);
    setPsor(license?.stateOfResidence);
    setPsorOld(license?.stateOfResidence);
    setStateLicenses(licenses?.length ? licenses : [new EmployeeLicense({})]);
  }, [license]);

  useEffect(() => {
    const newCompactLicense = compactLicense && !compactLicenseDisabled;
    if (!psorOld) {
      setPsorOld(psor);
      return;
    }
    if (!compactLicenseOld && !newCompactLicense) return;
    if (!stateLicenses?.length || !psor) return;

    const compact = stateLicenses.find(({ state }) => state === COMPACT);

    if (compact) {
      if (!newCompactLicense) {
        const compactActiveStatus =
          compact.status &&
          ![
            EmployeeLicenseStatus.INACTIVE,
            EmployeeLicenseStatus.EXPIRED,
          ].includes(compact.status);

        const oldLicenseNotAddedYet = !stateLicenses?.find(
          ({ licenseNumber, state }) =>
            state === psorOld && licenseNumber === compact.licenseNumber,
        );

        const newLicenseNotAddedYet =
          psor !== psorOld &&
          !stateLicenses?.find(({ state }) => state === psor);

        compact.status = compactLicenseDisabled
          ? EmployeeLicenseStatus.INACTIVE_DUE_PSOR
          : EmployeeLicenseStatus.INACTIVE;
        if (compactActiveStatus && compactLicenseDisabled) {
          setStateLicenses(
            [
              newLicenseNotAddedYet &&
                Object.assign(new EmployeeLicense({}), {
                  state: psor,
                }),
              oldLicenseNotAddedYet &&
                Object.assign(
                  new EmployeeLicense({}),
                  license?.compactLicense || {},
                  {
                    state: psorOld,
                  },
                ),
              ...stateLicenses,
            ].filter(Boolean) as EmployeeLicense[],
          );
        } else {
          setStateLicenses([...stateLicenses]);
        }
      } else {
        // Filter out Compact States
        setStateLicenses([
          {
            ...compact,
            status:
              (compact.status === EmployeeLicenseStatus.INACTIVE_DUE_PSOR
                ? EmployeeLicenseStatus.ACTIVE
                : compact.status) || EmployeeLicenseStatus.INACTIVE_DUE_PSOR,
          },
          ...stateLicenses.filter(
            ({ state }) =>
              state !== COMPACT &&
              state &&
              !data.compactStates?.includes(state),
          ),
        ]);
      }
    } else {
      // Add Compact States
      setStateLicenses([
        Object.assign(new EmployeeLicense({}), license?.compactLicense || {}, {
          state: COMPACT,
        }),
        ...stateLicenses.filter(
          ({ state }) =>
            state !== COMPACT && state && !data.compactStates?.includes(state),
        ),
      ]);
    }

    setCompactLicense(newCompactLicense);
    setCompactLicenseOld(newCompactLicense);
    setPsorOld(psor);
  }, [compactLicense, compactLicenseDisabled, psor]);

  const validateLicense = (li: EmployeeLicense) =>
    li.state &&
    li.status &&
    (li.status !== EmployeeLicenseStatus.EXPIRED || li.expiresAt);

  const validate = () => !!psor && stateLicenses.every(validateLicense);

  const doSubmit = async () => {
    setSubmitted(true);
    if (!validate() || !license) return;
    setLoadingButton(true);

    try {
      const compactLicenseItem = stateLicenses?.find(
        ({ state }) => state?.value === COMPACT.value,
      );

      const otherLicenses = (stateLicenses || []).filter(
        ({ state }) => state?.value !== COMPACT.value,
      );

      await onSubmit(license.employee?.id, {
        compactLicense: compactLicenseItem
          ? {
              licenseNumber: compactLicenseItem.licenseNumber || undefined,
              expiresAt: compactLicenseItem.expiresAt?.format(DATE_FORMAT),
              isNlc: true,
              issuedAt: compactLicenseItem.issuedAt?.format(DATE_FORMAT),
              status:
                compactLicenseItem.status?.value ||
                EmployeeLicenseStatus.INACTIVE.value,
            }
          : undefined,
        stateOfResidence: psor?.value as string,
        stateLicenses:
          otherLicenses?.map((license) => ({
            licenseNumber: license.licenseNumber || undefined,
            expiresAt: license.expiresAt?.format(DATE_FORMAT),
            isNlc: false,
            issuedAt: license.issuedAt?.format(DATE_FORMAT),
            state: license.state?.value,
            status: license.status?.value as string,
          })) || [],
      });
      onClose();
      setLoadingButton(false);
    } catch (e) {
      showGlobalError(e as string);
      setLoadingButton(false);
    }
  };

  return (
    <CollapsableSidebar
      title={
        <h2>
          {translate('employees.modal.titleLicenseEdit', {
            employee: license?.employee?.fullName || 'Employee',
          })}
        </h2>
      }
      onClose={onClose}
      open={open}
      size={550}
    >
      <CollapsableSidebar.Body>
        <div className="grid-wrapper fit">
          <Select
            className="grid-span-6"
            data-cy="employees-license-modal-role"
            disableClearable
            error={submitted && !psor}
            getItemLabel={(state?: States) => state?.toString()}
            filterOptions={(
              data: Selectable[],
              { inputValue }: { inputValue: string },
            ) =>
              data.filter(
                (item: Selectable) =>
                  (item.value as string)
                    ?.toLocaleLowerCase()
                    .includes(inputValue?.toLocaleLowerCase()) ||
                  (item.label as string)
                    ?.toLocaleLowerCase()
                    ?.includes(inputValue?.toLocaleLowerCase()),
              )
            }
            items={States.asArray}
            label={translate('employees.modal.psor')}
            onChange={setPsor}
            value={psor}
            required
          />

          <SwitchGroup
            className="grid-span-12"
            items={
              [
                {
                  label: translate('employees.modal.compactLicense'),
                  checked: compactLicense && !compactLicenseDisabled,
                  disabled: compactLicenseDisabled,
                },
              ] as SwitchItem[]
            }
            onChange={(items) => {
              setCompactLicense(!!items[0].checked);
            }}
          />
          <EmployeLicensesEdit
            licenses={stateLicenses}
            onChange={setStateLicenses}
            showCompact={compactLicense && !compactLicenseDisabled}
            submitted={submitted}
          />
        </div>
      </CollapsableSidebar.Body>
      <CollapsableSidebar.Buttons>
        <Button
          color="tertiary"
          data-cy="edit-employee-license-close"
          onClick={onClose}
        >
          {translate('global.close')}
        </Button>
        <Button
          color="secondary"
          data-cy="edit-employee-license-submit"
          loading={loadingButton}
          onClick={doSubmit}
        >
          {translate('global.save')}
        </Button>
      </CollapsableSidebar.Buttons>
    </CollapsableSidebar>
  );
};

export default EmployeeLicenseEdit;
